import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getEventDetail } from './events';
import type { EventDetailDto, FavoriteItemDto } from '@/services/platform/api/model';

export interface FavoriteEvent {
  item: FavoriteItemDto;
  /** Live event data for `item.itemExternalId`. Undefined if the fetch failed (e.g. the event was unpublished). */
  event: EventDetailDto | undefined;
}

export interface UseFavoriteEventsResult {
  data: FavoriteEvent[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Enriches Events favorite items with live event data (category, venue, seats, dates), fetched
 * in parallel by `itemExternalId` — mirrors `useFavoriteShopProducts`, there is no batch endpoint.
 */
export function useFavoriteEvents(items: FavoriteItemDto[]): UseFavoriteEventsResult {
  const locale = useLocaleStore(state => state.locale);
  const [data, setData] = useState<FavoriteEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const itemsKey = useMemo(() => items.map(i => i.publicGuid).join(','), [items]);

  const refetch = useCallback(async () => {
    if (items.length === 0) {
      setData([]);
      return;
    }
    setIsLoading(true);
    try {
      const results = await Promise.all(
        items.map(async item => {
          if (!item.itemExternalId) return { item, event: undefined };
          try {
            const event = await getEventDetail(item.itemExternalId, locale);
            return { item, event };
          } catch {
            return { item, event: undefined };
          }
        })
      );
      setData(results);
    } finally {
      setIsLoading(false);
    }
    // itemsKey captures item identity changes; items itself is a new array each render.
  }, [itemsKey, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, refetch };
}
